import { h, Component } from 'preact';

import { joinClasses, accessNested, sortObjectByOrder } from 'utils/utils';
import { getValueForClientSize, getTTSizeOverride, navigateInternally, splitQueryAndPath } from 'utils/bolt';
import { getIsSinglePageIframed } from 'utils/bolt/client';
import { useDynamicAssetFolder } from 'utils/assets';
import tryToPlayVideo from 'utils/tryToPlayVideo';
import HTML from 'components/core/html/html.js';
import * as webp from 'utils/bolt/webp';
import { isWebPSupported } from 'utils/isWebPSupported';
import Button from 'components/bolt/button/button';
import Countdown from 'components/bolt/countdown/countdown';
import QuotesCarousel from 'components/bolt/quotesCarousel/quotesCarousel';
import Store from 'store/store';

import s from 'components/bolt/movieDetails/movieDetails.sss';

const loadedImages = {};

export default class MovieDetails extends Component {
	constructor(props) {
		super(props);

		this.state = {
			posterVisible: false,
			bgVideo: false,
			backgroundStyle: {},
			bgImageLoaded: false,
			quotesCarouselPosition: 'top'
		};

		this.hidePoster = this.hidePoster.bind(this);
		this.removeBgVideo = this.removeBgVideo.bind(this);
		this.updateBgStyle = this.updateBgStyle.bind(this);
		this.getBgImage = this.getBgImage.bind(this);
		this.getFallbackFormat = this.getFallbackFormat.bind(this);
		this.stickAndScale = this.stickAndScale.bind(this);
		this.renderButton = this.renderButton.bind(this);

		Store.on('STICK_AND_SCALE', this.stickAndScale);
	}

	componentDidMount() {
		window.addEventListener('resize', this.updateBgStyle);
		this.updateBgStyle();
		const quotesCarousel = this.props.appData?.pages?.boltHome?.data?.quotesCarousel;
		const hasQuotesCarousel = quotesCarousel && this.props.type === 'home';
		if (hasQuotesCarousel) {
			this.setQuotesCarouselPosition();
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.cookieHeight !== this.props.cookieHeight) {
			this.updateBgStyle();
		}
		if (this.props.client?.deviceSize !== prevProps.client?.deviceSize) {
			this.setQuotesCarouselPosition();
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.updateBgStyle);
		Store.off('STICK_AND_SCALE', this.stickAndScale);
	}

	setQuotesCarouselPosition() {
		const position = getValueForClientSize(this.props.appData?.pages?.boltHome?.data?.quotesCarousel?.positioning) || 'top';
		this.setState({
			quotesCarouselPosition: position
		});
	}

	updateBgStyle() {
		const backgroundStyle = {
			backgroundImage: this.getBgImage(),
			backgroundSize: 'cover',
			backgroundPosition: 'center'
		};

		const store = Store.get();
		const isMobile = !accessNested(store, 'client.isDesktopSize', false);
		const isSinglePageIframed = getIsSinglePageIframed();
		const sonyHeaderEnabled = !isSinglePageIframed && accessNested(store, 'appData.options.sonyFeatures.sonyHeader', false);

		if (!isMobile && this.props.cookieBannerOpen && this.props.type !== 'home') {
			let paddingTop = (this.props.cookieBannerOpen && this.props.type === 'home') ? this.props.cookieHeight : 0;

			if (sonyHeaderEnabled) {
				paddingTop += 34;
			}

			backgroundStyle.width = ((window.innerHeight - this.props.cookieHeight - paddingTop) / 900 * 609) + 'px';
		}

		this.setState({ backgroundStyle });
	}

	getFallbackFormat(value) {
		const page = { homeEnt: 'boltHomeEnt', showtimes: 'boltShowtimes', home: 'boltHome' }[this.props.type];
		const defaultFormat = value === 'tt' ? 'png' : 'jpg';
		return accessNested(this.props, `appData.pages.${page}.data.${value}.alternativeImageFormat`, defaultFormat);
	}

	getBgImage() {
		let asset = this.getBgValue('asset');
		if (!asset) {
			return '';
		}
		const fallbackFormat = this.getFallbackFormat('bg');

		// If asset is a video, bg image will be the fallback image instead
		if (/\.(mp4|webm)$/.test(asset)) {
			this.setState({ bgVideo: asset });
			asset = this.getBgValue('fallbackImg');
		}

		// Makes sure the background image is fully loaded before showing it
		if (!loadedImages[asset]) {
			loadedImages[asset] = true;
			let onLoad = () => {
				if (this.state.bgImageLoaded) {
					return;
				}
				this.setState({ bgImageLoaded: true });

				// DO SOMETHING WITH BG IMAGE LOADED
			};
			const image = new Image();
			image.onload = onLoad;
			image.onerror = onLoad;
			image.src = asset;
			// If the image takes too long to load, better to show the content
			setTimeout(onLoad, 5000);
		}

		return webp.css(asset, fallbackFormat);
	}

	getBgValue(field) {
		const backgroundData = this.props.bg;

		if (!backgroundData || !backgroundData.desktop && !backgroundData.tablet && !backgroundData.mobile) {
			return undefined;
		}

		let bgValue = {};

		Object.entries(backgroundData).forEach(([device, data]) => {
			bgValue[device] = data[field];
		});

		return useDynamicAssetFolder(getValueForClientSize(bgValue));
	}

	getTTPosition(tt) {
		if (!tt || !tt.titleTreatmentPosition || (!tt.titleTreatmentPosition.desktop && !tt.titleTreatmentPosition.tablet && !tt.titleTreatmentPosition.mobile)) {
			return null;
		}
		return getValueForClientSize(tt.titleTreatmentPosition);
	}

	getTT(tt) {
		if (!tt || !tt.desktop && !tt.tablet && !tt.mobile) {
			return null;
		}
		const ttSrc = getValueForClientSize(tt, null, { nullIfDisabled: true });
		return useDynamicAssetFolder(ttSrc);
	}

	hidePoster() {
		this.setState({ posterVisible: false });
	}

	removeBgVideo() {
		this.setState({ bgVideo: false });
	}

	playVideo(e) {
		tryToPlayVideo(e.target).then(this.hidePoster).catch(this.removeBgVideo);
	}

	trackButtonClick(path, label) {
		Store.emit('MOVIE_DETAILS_BUTTON_CLICK', this.props.type, label, path);
	}

	triggerButton(btnPath, label) {
		this.trackButtonClick(btnPath, label);
		const { path, query } = splitQueryAndPath(btnPath);
		navigateInternally(path, { instant: false, queryString: query });
	}

	stickAndScale(percentage) {
		if (!Store.get().client.isDesktopSize) {
			const maxTop = 16;
			const top = maxTop - (maxTop * percentage);
			this.wrapper.style.top = top + 'px';
			return this.wrapper.style.borderRadius = 20 - (20 * percentage) + 'px';
		}

		this.wrapper.style.top = '0px';
		this.wrapper.style.borderRadius = 0;
	}

	renderTT(tt = {}) {
		let { type, visible, hide } = this.props;
		let ttStyle;
		if (type === 'homeEnt') {
			type = 'showtimes';
		}
		// use hide specifically when you need it to fade out instead of disappear
		const ttSrc = this.getTT(tt);
		const ttSize = getTTSizeOverride(tt);
		const ttPos = this.getTTPosition(tt);
		const ttRightOrLeft = ttPos === 'right' || ttPos === 'left';

		if (ttSize) {
			ttStyle = {
				width: ttSize.maxWidth ? (ttRightOrLeft ? 'auto' : '100%') : null,
				height: ttSize.maxHeight ? '100%' : null,
				maxWidth: ttSize.maxWidth,
				maxHeight: ttSize.maxHeight
			};
		}
		if (!ttSrc) {
			return;
		}

		const fallbackFormat = this.getFallbackFormat('tt');
		const copy = this.props.copy;
		return (
			<webp.Picture
				style={ttStyle}
				class={joinClasses(s.tt, s[type], visible && s.visible, hide && s.hide)}
				alt={copy.accessibilityCopy[tt.alt] || tt.alt}
				src={ttSrc}
				fallbackFormat={fallbackFormat}
			/>
		);
		// return type === 'home' ? <h1>{image}</h1> : image;
	}

	renderCountdown(countdown, position) {
		let { hide, visible } = this.props;
		const { targetDateTime, annotation, timeZone = '0' } = countdown;
		const appData = this.props.appData;
		const headerHeight = Store.get().client?.headerHeight || 0;

		return (
			<Countdown
				appData={appData}
				datetime={targetDateTime}
				timeOffset={timeZone}
				annotation={annotation}
				hidden={hide}
				visible={visible}
				position={position}
				headerHeight={headerHeight}
			/>
		);
	}

	onClick(e) {
		let internalLink = e.target.closest('a[href^="#$"]');
		if (internalLink) {
			e.preventDefault();
			let page = internalLink.getAttribute('href').slice(2).trim();
			let navOptions = {};
			if (page.includes('/?')) {
				let [pageId, qs] = page.split('/?');
				page = pageId;
				navOptions.queryString = '?' + qs;
			}
			navigateInternally(page, navOptions);
		}
	}

	renderTextAboveButtons() {
		const store = Store.get();
		const { visible, hide, copy, appData } = this.props;
		const fontStyles = accessNested(appData, 'styling.fontVariables') || {};
		const activePage = store.activePage;
		const textAboveButtons = accessNested(store, 'appData.pages.boltHome.data.textAboveButtons', '');
		const copyAboveCta = copy.baseCopy[textAboveButtons] || textAboveButtons;
		const splashHubTextAboveButtons = accessNested(store, 'appData.pages.boltSplashHub.data.buttons.textAboveButtons', '');
		const splashHubCopyAboveCta = copy.baseCopy[splashHubTextAboveButtons] || splashHubTextAboveButtons;

		if (textAboveButtons && activePage === 'boltHome') {
			return (
				<div class={joinClasses(s.textAboveButtons, visible && s.visible, hide && s.hide, (fontStyles.homeTextAboveButtons || 'xlarge'))}>
					<HTML markdown class={s.copyAboveButtons} onClickCapture={this.onClick} content={copyAboveCta} />
				</div>
			);
		}
		if (splashHubTextAboveButtons && activePage === 'boltSplashHub') {
			return (
				<div class={joinClasses(s.textAboveButtons, visible && s.visible, hide && s.hide, (fontStyles.homeTextAboveButtons || 'xlarge'))}>
					<HTML markdown tag="h4" class={s.copyAboveButtons} onClickCapture={this.onClick} content={splashHubCopyAboveCta} />
				</div>
			);
		}
	}

	renderButton(button) {
		if (!button) {
			return;
		}
		const appData = this.props.appData;
		let page = this.props.appData.pages[button.path];

		if (button.path?.includes('?')) {
			const pageId = button.path.split('?')[0];
			page = this.props.appData.pages[pageId];
		}
		if (!button.path.startsWith('@') && !page && !button.path.match(/^https?:\/\/|mailto/)) {
			return;
		}
		if (page?.ageRestricted && this.props.client?.ofAge === false) {
			return;
		}
		let label = this.props.copy.baseCopy[button.label] || button.label;
		const content = (
			<HTML
				class={s.buttonLabel}
				markdown
				content={label}
			/>
		);

		const buttonClass = appData?.styling?.fontVariables?.homeButtons;
		const buttonStyle = appData?.styling?.fontVariables?.homeButtonfontSize ? { fontSize: appData.styling.fontVariables.homeButtonfontSize } : {};
		const attributes = { id: button.id, class: joinClasses(s.button, buttonClass), style: buttonStyle, key: button.id || button.path };
		if (/^(https?:\/\/|mailto:)/i.test(button.path)) {
			return (
				<a
					{...attributes}
					href={button.path}
					target={button.sameWindow ? '_self' : '_blank'}
					rel="noopener noreferrer"
					onClick={() => this.trackButtonClick(button.path, label)}
				>
					{content}
				</a>
			);
		}
		return (
			<Button {...attributes} trigger={() => this.triggerButton(button.path, label)} type="floating">
				{content}
			</Button>
		);
	}

	renderButtons(buttons) {
		const { appData, visible, hide, buttonLayout = 'row' } = this.props;
		const allButtonStyling = accessNested(appData, 'styling.enableDisableAllButtonStyling.homeButton');

		const sortedButtons = sortObjectByOrder(buttons);
		if (!sortedButtons.length) {
			return;
		}

		return (
			<div class={joinClasses(s.buttons, allButtonStyling && s.allStyling, visible && s.visible, hide && s.hide, s[buttonLayout])}>
				{sortedButtons.map(this.renderButton)}
			</div>
		);
	}

	renderBgVideo() {
		const { bgVideo, posterVisible } = this.state;
		const preLoadImg = this.getBgValue('preLoadImg');
		const fallbackFormat = this.getFallbackFormat('bg');

		return (
			<div class={joinClasses({ s, bgVideo: true })} key="bgVideo">
				{posterVisible && <webp.Picture alt="" src={preLoadImg} fallbackFormat={fallbackFormat} />}
				<video
					muted
					autoPlay
					playsInline
					loop
					poster={isWebPSupported ? preLoadImg : preLoadImg.replace(/\.[^.]*$/, fallbackFormat)}
					onError={this.removeBgVideo}
					onCanPlay={this.playVideo}
					ref={$ref => this.$bgVideo = $ref}
				>
					{bgVideo.includes('.webm') && <source src={bgVideo} type="video/webm" />}
					<source src={bgVideo.replace(/\.[^.]*$/, '.mp4')} type="video/mp4" />
				</video>
			</div>
		);
	}

	renderQuotesCarousel(carousel) {
		return (
			<QuotesCarousel
				data={carousel}
				client={this.props.client}
				position={this.state.quotesCarouselPosition}
			/>
		);
	}

	renderExtraInMiddle() {
		return;
	}

	renderExtraDetail() {
		return;
	}


	render({ type, tt, bg, buttons, cookieHeight = 0, cookieBannerOpen, dualViewEnabled, isWidget }, { bgVideo, backgroundStyle, quotesCarouselPosition }) {
		const isSinglePageIframed = getIsSinglePageIframed();

		const detailPosition = this.getTTPosition(tt);
		const store = Store.get();
		const sonyHeaderEnabled = !isSinglePageIframed && accessNested(store, 'appData.options.sonyFeatures.sonyHeader', false);
		const disableTTAnimation = !!(dualViewEnabled && accessNested(store, 'appData.pages.boltHomeEnt.data.tt') && accessNested(store, 'appData.pages.boltShowtimes.data.tt'));
		let paddingTop = (cookieBannerOpen && type === 'home') ? cookieHeight : 0;
		let paddingBottom = 0;

		if (sonyHeaderEnabled) {
			paddingTop += 34;
		}

		const externalBannerOpen = accessNested(store, 'externalCookieBanner.bannerOpen', false);
		const isMobile = !accessNested(store, 'client.isDesktopSize', false);
		const oneContinuousScroll = (externalBannerOpen && isMobile || accessNested(store, 'appData.options.oneContinuousScroll')) && type === 'home';

		if (oneContinuousScroll) {
			paddingTop = 0;
		}

		const showtimesShowing = accessNested(store, 'showtimes.show', false);
		const homeEntShowing = accessNested(store, 'homeEnt.show', false);
		const pressKitShowing = accessNested(store, 'pressKit.show', false);
		const footerType = accessNested(store, 'appData.footer.defaultFooter.position', 'fixed');

		if (!showtimesShowing && !homeEntShowing && !pressKitShowing && !oneContinuousScroll) {
			paddingBottom = !isMobile ? 40 : (footerType === 'fixed' ? 72 : 32);
			if (isWidget) {
				paddingBottom = 0;
			}
		}

		const countdownClock = accessNested(store, 'appData.options.countdownClock', false);
		const hasCountdown = countdownClock && type === 'home';
		const countdownPos = countdownClock?.position || 'standard';
		const hasMiniGallery = accessNested(store, 'appData.options.paramountFeatures.toutCarousel.contentCarousel', false);
		const activePage = accessNested(store, 'activePage', '');
		const isHubPage = accessNested(store, `appData.pages.${activePage}.component`, '') === 'hub';

		const quotesCarousel = this.props.appData?.pages?.boltHome?.data?.quotesCarousel;
		const quotesCarouselAllowedRegions = (quotesCarousel?.regions || '').split(',').map(a => a.trim()).filter(a => a);
		const clientRegion = this.props.client?.geo?.countryCode;
		const hasQuotesCarousel = quotesCarousel && type === 'home' && (quotesCarouselAllowedRegions.length < 1 || quotesCarouselAllowedRegions.includes(clientRegion));
		const quotesCarouselAnchoredToTT = quotesCarouselPosition === 'aboveTT' || quotesCarouselPosition === 'belowTT';

		return (
			<div
				class={joinClasses(
					s.wrapper,
					this.props.class,
					hasMiniGallery && s.miniGallery,
					isWidget && s.widget
				)}
				style={{
					paddingTop: paddingTop + 'px',
					paddingBottom: paddingBottom + 'px',
					position: oneContinuousScroll ? 'absolute' : 'fixed'
				}}
				ref={ref => this.wrapper = ref}
			>
				<div
					class={joinClasses(
						s.container,
						s[type],
						sonyHeaderEnabled && s.sonyHeader
					)}
					style={bg && backgroundStyle}
				>
					{(bgVideo && bg) && this.renderBgVideo()}
					{(hasCountdown && countdownPos === 'top') && this.renderCountdown(countdownClock, countdownPos)}
					{hasQuotesCarousel && !quotesCarouselAnchoredToTT && this.renderQuotesCarousel(quotesCarousel)}
					<div class={joinClasses(s.details, s[type], s[detailPosition], disableTTAnimation && s.noAnimation)}>
						{!isHubPage && this.renderTT(tt)}
						{hasQuotesCarousel && quotesCarouselAnchoredToTT && this.renderQuotesCarousel(quotesCarousel)}
						{(hasCountdown && countdownPos === 'standard') && this.renderCountdown(countdownClock, countdownPos)}
						{this.renderExtraInMiddle()}
						{!isHubPage && !isSinglePageIframed && this.renderTextAboveButtons()}
						{!isHubPage && !isSinglePageIframed && this.renderButtons(buttons)}
						{this.renderExtraDetail()}
					</div>
				</div>
			</div>
		);
	}
}